'use client';

import { c as _c } from "react/compiler-runtime";
import { Spinner } from '@common/components';
import { cn } from '@core/utils';
import type { QueryKey } from '@tanstack/react-query';
import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import React, { useState } from 'react';
interface InfiniteListWithMoreButtonProps<TData> {
  /**
   * React Query에서 사용할 쿼리 키
   * 이 키를 기준으로 쿼리 캐싱 및 무효화가 이루어짐
   */
  queryKey: QueryKey;
  /**
   * 데이터를 가져오는 비동기 함수
   * @param pageParam - 현재 페이지 파라미터(오프셋)
   * @returns Promise<TData[]> - 데이터 배열을 반환하는 Promise
   */
  queryFn: ({
    pageParam
  }: {
    pageParam: number;
  }) => Promise<TData[]>;
  /**
   * 각 아이템을 렌더링하는 함수
   * @param item - 렌더링할 아이템 데이터
   * @param index - 아이템의 인덱스
   * @returns ReactNode - 렌더링할 컴포넌트
   */
  renderItem: (item: TData, index: number) => ReactNode;
  /**
   * 각 아이템의 고유 키를 생성하는 함수
   * 제공되지 않을 경우 기본적으로 item.id 또는 index를 사용
   * @param item - 키를 생성할 아이템 데이터
   * @param index - 아이템의 인덱스
   * @returns string | number - 고유 키 값
   */
  getItemKey: (item: TData, index: number) => string | number;
  /**
   * 한 페이지에 표시할 아이템 수
   */
  pageSize: number;
  /**
   * 로딩 상태를 표시하는 컴포넌트
   */
  loadingComponent?: ReactNode;
  /**
   * 추가 데이터 로딩 상태를 표시하는 컴포넌트
   */
  fetchingNextPageComponent?: ReactNode;
  /**
   * 더보기 버튼
   */
  moreButtonComponent: ReactNode;
  moreButtonClassName?: string;
  /**
   * 초기 데이터
   * 서버/초기 렌더링 시 HTML에 포함
   */
  initialData?: TData[];
  /**
   * 조회 결과가 0건일 때 표시할 컴포넌트
   */
  emptyComponent?: ReactNode;
  //* 쿼리 실행 여부(기본값 true)
  enabled?: boolean;
}
export const InfiniteListWithMoreButton = t0 => {
  const $ = _c(49);
  const {
    queryKey,
    queryFn,
    renderItem,
    getItemKey,
    pageSize,
    loadingComponent,
    fetchingNextPageComponent,
    moreButtonComponent,
    moreButtonClassName,
    initialData,
    emptyComponent,
    enabled: t1
  } = t0;
  const enabled = t1 === undefined ? true : t1;
  const [initialQueryKey] = useState(queryKey);
  const isQueryKeyChanged = JSON.stringify(queryKey) !== JSON.stringify(initialQueryKey);
  const shouldUseInitialData = !isQueryKeyChanged ? initialData : undefined;
  let t2;
  if ($[0] !== queryFn) {
    t2 = t3 => {
      const {
        pageParam
      } = t3;
      return queryFn({
        pageParam
      });
    };
    $[0] = queryFn;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== pageSize) {
    t3 = (lastPage, _allPages, lastPageParam) => {
      if (!lastPage || lastPage.length === 0 || lastPage.length < pageSize) {
        return;
      }
      return lastPageParam + 1;
    };
    $[2] = pageSize;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== initialData || $[5] !== shouldUseInitialData) {
    t4 = shouldUseInitialData ? {
      pages: [initialData],
      pageParams: [1]
    } : undefined;
    $[4] = initialData;
    $[5] = shouldUseInitialData;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== enabled || $[8] !== queryKey || $[9] !== t2 || $[10] !== t3 || $[11] !== t4) {
    t5 = {
      queryKey,
      queryFn: t2,
      getNextPageParam: t3,
      initialPageParam: 1,
      placeholderData: keepPreviousData,
      initialData: t4,
      enabled
    };
    $[7] = enabled;
    $[8] = queryKey;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
    isPlaceholderData
  } = useInfiniteQuery(t5);
  let t6;
  if ($[13] !== data?.pages) {
    t6 = data?.pages?.flat().filter(_temp) ?? [];
    $[13] = data?.pages;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const loadedItems = t6;
  const isWaitingToFetch = !enabled && loadedItems.length === 0;
  const isEmpty = !isLoading && !isWaitingToFetch && !isError && !isPlaceholderData && loadedItems.length === 0;
  let t7;
  if ($[15] !== getItemKey || $[16] !== renderItem || $[17] !== shouldUseInitialData) {
    t7 = shouldUseInitialData?.map((item_0, index) => <React.Fragment key={getItemKey(item_0, index)}>{renderItem(item_0, index)}</React.Fragment>);
    $[15] = getItemKey;
    $[16] = renderItem;
    $[17] = shouldUseInitialData;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== data?.pages || $[20] !== getItemKey || $[21] !== renderItem || $[22] !== shouldUseInitialData) {
    t8 = data?.pages?.slice(shouldUseInitialData ? 1 : 0).flat().filter(_temp2).map((item_2, index_0) => <React.Fragment key={getItemKey(item_2, index_0)}>{renderItem(item_2, index_0)}</React.Fragment>);
    $[19] = data?.pages;
    $[20] = getItemKey;
    $[21] = renderItem;
    $[22] = shouldUseInitialData;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  let t9;
  if ($[24] !== isLoading || $[25] !== isWaitingToFetch || $[26] !== loadingComponent) {
    t9 = (isLoading || isWaitingToFetch) && (loadingComponent || <div className="flex-center" style={{
      gridColumn: "1 / -1"
    }}><Spinner /></div>);
    $[24] = isLoading;
    $[25] = isWaitingToFetch;
    $[26] = loadingComponent;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  let t10;
  if ($[28] !== fetchingNextPageComponent || $[29] !== isFetchingNextPage) {
    t10 = isFetchingNextPage && (fetchingNextPageComponent || <div className="flex-center" style={{
      gridColumn: "1 / -1"
    }}><Spinner /></div>);
    $[28] = fetchingNextPageComponent;
    $[29] = isFetchingNextPage;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  let t11;
  if ($[31] !== fetchNextPage || $[32] !== hasNextPage || $[33] !== moreButtonClassName || $[34] !== moreButtonComponent) {
    t11 = hasNextPage !== false && React.isValidElement(moreButtonComponent) && <div className={cn("flex justify-center", moreButtonClassName)} style={{
      gridColumn: "1 / -1"
    }}>{React.cloneElement(moreButtonComponent as React.ReactElement<Record<string, unknown>>, {
        onClick: fetchNextPage
      })}</div>;
    $[31] = fetchNextPage;
    $[32] = hasNextPage;
    $[33] = moreButtonClassName;
    $[34] = moreButtonComponent;
    $[35] = t11;
  } else {
    t11 = $[35];
  }
  let t12;
  if ($[36] !== emptyComponent || $[37] !== isEmpty) {
    t12 = isEmpty && emptyComponent ? <div style={{
      gridColumn: "1 / -1"
    }}>{emptyComponent}</div> : null;
    $[36] = emptyComponent;
    $[37] = isEmpty;
    $[38] = t12;
  } else {
    t12 = $[38];
  }
  let t13;
  if ($[39] !== isError) {
    t13 = isError && <div className="p-16 text-center text-R-500" style={{
      gridColumn: "1 / -1"
    }}>목록을 불러오지 못했어요. 잠시 후 다시 시도해 주세요.</div>;
    $[39] = isError;
    $[40] = t13;
  } else {
    t13 = $[40];
  }
  let t14;
  if ($[41] !== t10 || $[42] !== t11 || $[43] !== t12 || $[44] !== t13 || $[45] !== t7 || $[46] !== t8 || $[47] !== t9) {
    t14 = <>{t7}{t8}{t9}{t10}{t11}{t12}{t13}</>;
    $[41] = t10;
    $[42] = t11;
    $[43] = t12;
    $[44] = t13;
    $[45] = t7;
    $[46] = t8;
    $[47] = t9;
    $[48] = t14;
  } else {
    t14 = $[48];
  }
  return t14;
};
function _temp(item) {
  return item !== undefined;
}
function _temp2(item_1) {
  return item_1 !== undefined;
}